/**
 * Validate Deals Actor
 *
 * XState actor that validates deal data before import.
 * Checks:
 * 1. Deal product column exists (required)
 * 2. Deal products are valid (if column exists)
 * 3. Account owners are valid (if column exists)
 * 4. Sales stages are valid (if column exists)
 *
 * Returns validation data if failed, null if passed.
 */
import { fromPromise } from 'xstate'
import {
  validateDealAccountOwners,
  validateDealProducts,
  validateDealSalesStages,
  ValidateDealAccountOwnersResponse,
  ValidateDealProductsResponse,
  ValidateDealSalesStagesResponse,
} from '../../importer-api'
import { ImportData } from '../../importer-types'

export interface ValidateDealsInput {
  importNumber: number
  importData: ImportData | null
}

/** Error for missing/invalid values that need mapping via modal */
export interface ValidationMappingError {
  type: 'mapping'
  missing: string[]
  found: string[]
  title: string
  itemName: string
  columnName: 'deal_product' | 'deal_account_owner' | 'deal_sales_stage'
}

/** Error for missing deal_product column - needs product selection modal */
export interface ValidationProductRequiredError {
  type: 'product_required'
  title: string
  message: string
}

export type ValidationErrorData =
  | ValidationMappingError
  | ValidationProductRequiredError

export type ValidateDealsOutput = ValidationErrorData | null

export const validateDealsActor = fromPromise<
  ValidateDealsOutput,
  ValidateDealsInput
>(async ({ input }) => {
  if (!input.importNumber || !input.importData) {
    return null
  }

  // 1. Check if deal_product column exists (REQUIRED for deal import)
  const hasDealProduct = input.importData.headers.some(
    (h) => h.mapped === 'deal_product'
  )

  if (!hasDealProduct) {
    return {
      type: 'product_required',
      title: 'Deal Product Required',
      message:
        'The deal import requires a product. Please select a product to apply to all deals.',
    }
  }

  // 2. Validate products (if column exists - which it does at this point)
  const productResult: ValidateDealProductsResponse =
    await validateDealProducts(input.importNumber)

  if (!productResult.success) {
    return {
      type: 'mapping',
      missing: productResult.missing,
      found: productResult.found,
      title: 'Invalid Deal Products',
      itemName: 'Deal Product',
      columnName: 'deal_product',
    }
  }

  // 3. Validate account owners (if column exists)
  const hasAccountOwner = input.importData.headers.some(
    (h) => h.mapped === 'deal_account_owner'
  )

  if (hasAccountOwner) {
    const result: ValidateDealAccountOwnersResponse =
      await validateDealAccountOwners(input.importNumber)

    if (!result.success) {
      return {
        type: 'mapping',
        missing: result.missing,
        found: result.found,
        title: 'Invalid Deal Account Owners',
        itemName: 'Deal Account Owner',
        columnName: 'deal_account_owner',
      }
    }
  }

  // 4. Validate sales stages (if column exists)
  const hasSalesStage = input.importData.headers.some(
    (h) => h.mapped === 'deal_sales_stage'
  )

  if (hasSalesStage) {
    const result: ValidateDealSalesStagesResponse =
      await validateDealSalesStages(input.importNumber)

    if (!result.success) {
      return {
        type: 'mapping',
        missing: result.missing,
        found: result.found,
        title: 'Invalid Deal Sales Stages',
        itemName: 'Deal Sales Stage',
        columnName: 'deal_sales_stage',
      }
    }
  }

  // All validations passed
  return null
})
